/**
 * Android hardware back button and back gesture.
 *
 * Order of business on every press:
 *   1. a modal or confirm card that is open closes first (topmost wins);
 *   2. otherwise the active scene's own `onBack()` runs (level -> level select
 *      or menu, level select -> menu, result -> list, ad -> closed);
 *   3. a scene with no `onBack` is the root (the menu), so the app is left.
 * Boot has no UI yet, so a press there is swallowed rather than killing a
 * half-started app. Pure logic with no Phaser import so it can be tested in
 * plain node. The Capacitor App plugin is reached through registerPlugin so
 * the web bundle never needs it.
 */
import { Capacitor, registerPlugin } from '@capacitor/core';

interface BackScene { scene: { key: string }; onBack?: () => void }
interface BackGame { scene: { getScenes(isActive: boolean): BackScene[] } }

interface OverlayEntry { alive: () => boolean; close: () => void }
const overlays: OverlayEntry[] = [];

/**
 * Register an open overlay. `alive` must turn false once it is gone, however it
 * was dismissed, so a stale entry can never swallow a back press.
 */
export function registerOverlay(alive: () => boolean, close: () => void): void {
  overlays.push({ alive, close });
}

export type BackResult = 'overlay' | 'scene' | 'exit' | 'ignored';

export function handleBack(game: BackGame, exitApp: () => void): BackResult {
  while (overlays.length) {
    const top = overlays.pop()!;
    if (!top.alive()) continue;
    top.close();
    return 'overlay';
  }
  const active = game.scene.getScenes(true);
  for (let i = active.length - 1; i >= 0; i--) {
    const s = active[i];
    if (s.scene.key === 'Boot') return 'ignored';
    if (typeof s.onBack === 'function') { s.onBack(); return 'scene'; }
    if (s.scene.key === 'Menu') break;
  }
  exitApp();
  return 'exit';
}

interface AppPlugin {
  addListener(ev: 'backButton', cb: () => void): Promise<unknown>;
  exitApp(): Promise<void>;
}

/** Wire the native back button. No-op on the web. */
export function installBackButton(game: BackGame): void {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const App = registerPlugin<AppPlugin>('App');
    void App.addListener('backButton', () => {
      handleBack(game, () => { void App.exitApp(); });
    });
  } catch { /* plugin missing: default back behaviour stays */ }
}

/** Test hook. */
export function _resetOverlays(): void { overlays.length = 0; }
